import * as React from 'react';
import type { StepStatus, SyncView } from './steps.ts';

// The sync screen, presentational: the app's name, what is happening and
// why, the two steps with their state, and the ways out. Nothing here reads
// an API; index.tsx computes the view (steps.ts) and decides when to leave.

export interface SyncScreenProps {
  brandName: string;
  /** Whether this is the account's first sync (the long one) or a day's. */
  first: boolean;
  view: SyncView;
  /** The latest game is scanned: its review can be opened while the rest scan. */
  latest?: { label: string; onOpen: () => void };
  onContinue: () => void;
  onRetry: () => void;
}

const STATUS_LABEL: Record<StepStatus, string> = { waiting: 'Waiting', running: 'In progress', done: 'Done', failed: 'Failed' };

const Step: React.FC<{ n: number; title: string; body: string; step: SyncView['importStep'] }> = ({ n, title, body, step }) => (
  <li className={`sync-step sync-step-${step.status}`} aria-label={`${title}: ${STATUS_LABEL[step.status]}`}>
    <span className="sync-step-n" aria-hidden="true">
      {step.status === 'done' ? '✓' : step.status === 'failed' ? '!' : n}
    </span>
    <div className="sync-step-text">
      <h2 className="sync-step-title">{title}</h2>
      <p className="sync-step-body">{body}</p>
      <p className="sync-step-detail" role="status">
        {step.detail}
      </p>
    </div>
  </li>
);

export const SyncScreen: React.FC<SyncScreenProps> = ({ brandName, first, view, latest, onContinue, onRetry }) => (
  <div className="sync">
    <div className="sync-panel">
      <p className="sync-eyebrow">{brandName}</p>
      <h1 className="sync-title">{first ? 'Getting your games ready' : 'Checking for new games'}</h1>
      <p className="sync-lead">
        {first
          ? 'Your chess.com games of the last 30 days are coming in, and the engine reads each one on this device. The first time takes a few minutes; after that, only new games are read.'
          : 'Once a day, the games you played since the last visit come in and the engine reads them. Only the new ones, so this is quick.'}
      </p>
      <ol className="sync-steps">
        <Step n={1} title="Fetching your games" body="From chess.com, the last 30 days. Games already here are skipped." step={view.importStep} />
        <Step n={2} title="Reading them with the engine" body="Every move is checked, so the app can show where each game was won and lost." step={view.scanStep} />
      </ol>
      <div className="sync-actions">
        {view.importStep.status === 'failed' && (
          <button type="button" className="sync-button" onClick={onRetry}>
            Try again
          </button>
        )}
        {latest && (
          <button type="button" className="sync-button" onClick={latest.onOpen}>
            Open your latest game
          </button>
        )}
        {view.mayContinue && !view.complete && (
          <button type="button" className={`sync-button${view.importStep.status === 'failed' && !latest ? '' : ' sync-button-quiet'}`} onClick={onContinue}>
            {view.importStep.status === 'failed' ? `Continue to ${brandName}` : 'Continue while it finishes'}
          </button>
        )}
      </div>
      <p className="sync-note">
        {view.complete
          ? 'All set.'
          : view.scanStep.status === 'running' && !view.mayContinue
            ? 'As soon as your latest game is read you can go in; the rest are read in the background.'
            : view.scanStep.status === 'running'
              ? 'The reading goes on in the background if you continue.'
              : 'Everything stays on this device.'}
      </p>
    </div>
  </div>
);
